import { createSlice } from '@reduxjs/toolkit';

/**
 * このinitialStateはサーバー側でアプリケーションバージョンを作成したときに作られるものが正しいものになるので
 * 本来は不要だが、初回の表示の際にないとエラーになるのでここに存在する。
 */
const initialState = {
  lang: "ja",
  app_exp: '',
  app_header: `<meta charset="UTF-8">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<link rel="icon" href="favicon.ico" sizes="32x32">
<link rel="icon" href="images/icon.svg?@@pwappy_cache_buster@@" type="image/svg+xml">
<link rel="apple-touch-icon" href="images/apple-touch-icon.webp?@@pwappy_cache_buster@@">

<link rel="manifest" href="manifest.webmanifest?@@pwappy_cache_buster@@">

<!-- holderjs -->
<script async src="https://cdn.jsdelivr.net/npm/holderjs@2.9.9/holder.min.js"></script>

<!-- fontawesome v6 -->
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6/css/all.min.css" crossorigin="anonymous" referrerpolicy="no-referrer">

<!-- Onsen UI CSS -->
<link rel="stylesheet" href="https://unpkg.com/onsenui/css/onsenui-core.min.css">
<link rel="stylesheet" href="https://unpkg.com/onsenui/css/onsen-css-components.min.css">

<!-- OnsenUI -->
<script src="https://unpkg.com/onsenui/js/onsenui.min.js"></script>
<script>
    if (ons.platform.isIPhoneX()) {
        document.documentElement.setAttribute('onsflag-iphonex-portrait', '');
        document.documentElement.setAttribute('onsflag-iphonex-landscape', '');
    }
    ons.disableIconAutoPrefix();
</script>`,
  body_top: "",
  body_bottom: "",
  web_app_manifest: "",

  type_def_resource: "https://cdn.jsdelivr.net/npm/onsenui@2.12.8/esm/onsenui.d.ts",

  projectSpec: {
    specification: `# プロジェクト仕様書: [アプリ名を入力]

## 1. アプリの概要
- **目的**: ユーザーが[どのような課題]を解決し、[どのような目標]を達成するためのアプリか。

## 2. 実装機能
- [ ] 機能1: 
- [ ] 機能2: 

## 3. デザイン・UI指針
- **スタイル**: Onsen UIをベースとした、モダンで使いやすいモバイルデザイン。
- **ナビゲーション**: Navigatorによるスタック遷移を基本とする。

## 4. 開発・技術ルール（AI遵守事項）
- **イベント管理**: 
    - HTMLへのインライン記述（onclick等）は禁止。
    - 単純なクリック等は \`event_bindings\` を利用し、複雑な制御はページの \`init\` イベントに紐付けたスクリプト内で記述すること。
- **可読性の維持**:
    - すべての \`ons-page\` 等の主要タグに、役割を説明する \`explain\` 属性を日本語で付与すること。
- **データ管理**: 
    - データの保存が必要な場合は、ブラウザの \`sessionStorage\` 等を活用すること。
- **UIスタイリング**: 
    - selectタグおよびoptionタグを使用する際は、白背景・白文字で文字が消える現象を防ぐため、必ずCSSで背景色と文字色を明示的に指定すること。
`,
    locks: {
      pages: [],
      scripts: [],
      globalCss: false
    },
    pageMap: {}
  },
};

const appSettingSlice = createSlice({
  name: 'appSetting',
  initialState,
  reducers: {
    loadAppSettingData(state, action) {
      const payload = action.payload;
      // 1. ペイロードチェック
      if (!payload || typeof payload !== 'object') return;

      // 2. ルートプロパティのマージ
      Object.keys(initialState).forEach(key => {
        if (key !== 'projectSpec') {
          state[key] = payload[key] !== undefined ? payload[key] : initialState[key];
        }
      });

      // 3. projectSpec の安全なマージ
      const oldSpec = (payload.projectSpec && typeof payload.projectSpec === 'object') ? payload.projectSpec : {};
      const initSpec = initialState.projectSpec;

      state.projectSpec = {
        ...initSpec,
        ...oldSpec,

        // ネストされたオブジェクトの型保証とマージ
        locks: {
          ...initSpec.locks,
          ...((oldSpec.locks && typeof oldSpec.locks === 'object') ? oldSpec.locks : {})
        },
        pageMap: {
          ...initSpec.pageMap,
          ...((oldSpec.pageMap && typeof oldSpec.pageMap === 'object') ? oldSpec.pageMap : {})
        }
      };

      // 4. 必須項目の確認
      if (!state.projectSpec.specification) {
        state.projectSpec.specification = initSpec.specification;
      }
    },
    /**
     * 言語を更新
     * @param {*} state 
     * @param {*} action 
     */
    updateLang(state, action) {
      state.lang = action.payload;
    },

    /**
     * アプリの説明を更新
     * @param {*} state 
     * @param {*} action 
     */
    updateAppExp(state, action) {
      state.app_exp = action.payload;
    },
    /**
     * ヘッダーを更新
     * @param {*} state 
     * @param {*} action 
     */
    updateAppHeader(state, action) {
      state.app_header = action.payload;
    },

    /**
     * ボディタグ開始直後のリソース
     * @param {*} state 
     * @param {*} action 
     */
    updateBodyTop(state, action) {
      state.body_top = action.payload;
    },

    /**
     * ボディタグ終了直前のリソース
     * @param {*} state 
     * @param {*} action 
     */
    updateBodyBottom(state, action) {
      state.body_bottom = action.payload;
    },

    /**
     * webマニフェストを更新
     * @param {*} state 
     * @param {*} action 
     */
    updateWebAppManifest(state, action) {
      state.web_app_manifest = action.payload;
    },
    /**
     * 型定義を更新
     * @param {*} state 
     * @param {*} action 
     */
    updateTypeDefResource(state, action) {
      state.type_def_resource = action.payload;
    },

    /** 
    * Spec全体更新 (Raw編集用)
    */
    updateProjectSpec(state, action) {
      state.projectSpec = action.payload;
    },

    /**
     * 仕様書(Markdown)の更新
     */
    updateProjectSpecification(state, action) {
      if (!state.projectSpec) state.projectSpec = {};
      state.projectSpec.specification = action.payload;
    },

    /** 
     * グローバル設定の個別更新
     * @param {*} state 
     * @param {*} action 
     */
    updateSpecGlobal(state, action) {
      const { key, value } = action.payload;
      // 初期化漏れ対策
      if (!state.projectSpec.global) state.projectSpec.global = {};

      state.projectSpec.global[key] = value;
    },

    /** 
     * ページのロック切り替え
     * @param {*} state 
     * @param {*} action 
     */
    togglePageLock(state, action) {
      const { templateId, isLocked } = action.payload;
      if (!state.projectSpec.locks) state.projectSpec.locks = { pages: [], scripts: [] };

      const locks = state.projectSpec.locks.pages;
      if (isLocked) {
        if (!locks.includes(templateId)) locks.push(templateId);
      } else {
        state.projectSpec.locks.pages = locks.filter(id => id !== templateId);
      }
    },

    /** 
     * スクリプトのロック切り替え
     * @param {*} state 
     * @param {*} action 
     */
    toggleScriptLock(state, action) {
      const { scriptName, isLocked } = action.payload;
      if (!state.projectSpec.locks) state.projectSpec.locks = { pages: [], scripts: [] };

      const locks = state.projectSpec.locks.scripts;
      if (isLocked) {
        if (!locks.includes(scriptName)) locks.push(scriptName);
      } else {
        state.projectSpec.locks.scripts = locks.filter(name => name !== scriptName);
      }
    },

    /** 
     * ページの役割(Role)更新
     * @param {*} state 
     * @param {*} action 
     */
    updatePageRole(state, action) {
      const { templateId, role } = action.payload;
      if (!state.projectSpec.pageMap) state.projectSpec.pageMap = {};

      if (!state.projectSpec.pageMap[templateId]) {
        state.projectSpec.pageMap[templateId] = { role: "", status: "stable" };
      }
      state.projectSpec.pageMap[templateId].role = role;
    },

  },
});

export const {
  loadAppSettingData, updateLang, updateAppExp, updateAppHeader, updateBodyTop, updateBodyBottom, updateWebAppManifest, updateTypeDefResource,
  updateProjectSpec, updateProjectSpecification, updateSpecGlobal, togglePageLock, toggleScriptLock, updatePageRole
} = appSettingSlice.actions;
export default appSettingSlice.reducer;